import type { Address } from "../api/address";

type AddressComponent = {
  short_name?: string;
  long_name?: string;
  types?: Array<string>;
};

type Geometry = {
  location?: {
    lat?: number | (() => number);
    lng?: number | (() => number);
  };
};

export type Place = {
  address_components?: Array<AddressComponent>;
  place_id?: string;
  geometry?: Geometry;
  formatted_address?: string;
};

type PreFormatAddress = {
  lat: number;
  lng: number;
  place_id: string;
  street_number?: string | undefined;
  route?: string | undefined;
  locality?: string | undefined;
  administrative_area_level_1?: string | undefined;
  country?: string | undefined;
  postal_code?: string | undefined;
  formattedAddress: string;
}

interface PreFormatValidatedAddress {
  isValid: boolean;
  data: PreFormatAddress;
}

interface AddressComponentMap {
  [key: string]: AddressComponent;
}

type NameType = "short_name" | "long_name";

type MainComponentsType = {
  street_number: NameType;
  route: NameType;
  locality: NameType;
  administrative_area_level_1: NameType;
  country: NameType;
  postal_code: NameType;
};

const mainComponentNames: MainComponentsType = {
  street_number: "short_name",
  route: "short_name",
  locality: "long_name",
  administrative_area_level_1: "short_name",
  country: "short_name",
  postal_code: "short_name"
};

export type ValidatedAddress = {
  isValid: boolean;
  address?: Address;
};

const altComponents: { [key: string]: Array<string> } = {
  locality: [
    "neighborhood",
    "postal_town",
    "sublocality_level_1",
    "administrative_area_level_3"
  ],
  administrative_area_level_1: ["administrative_area_level_2"]
};

interface CountrySpecificBuilder {
  [key: string]: (addressComponentMap: AddressComponentMap) => string | null;
}

const countrySpecificBuilders: { [key: string]: CountrySpecificBuilder } = {
  JP: {
    street_number: (googleAddressComponents: AddressComponentMap) => {
      const { sublocality_level_3, sublocality_level_4, premise } =
        googleAddressComponents;

      const componentArray = [sublocality_level_3, sublocality_level_4, premise]
        .filter(Boolean)
        .map(({ short_name }) => short_name);

      return componentArray.length > 0 ? componentArray.join("-") : null;
    },
    route: (googleAddressComponents: AddressComponentMap) => {
      const { route, sublocality_level_2, sublocality_level_1 } =
        googleAddressComponents;

      const componentArray = [route, sublocality_level_2, sublocality_level_1]
        .filter(Boolean)
        .map(({ short_name }) => short_name);

      return componentArray.length > 0 ? componentArray.join(", ") : null;
    }
  }
};

export const getAddressComponentValue = (
  addressType: string,
  nameType: NameType,
  googleAddressComponents: AddressComponentMap
): string | undefined => {
  const country = googleAddressComponents.country.short_name;
  const addressKeys = altComponents[addressType] || [];
  addressKeys.unshift(addressType);

  if (
    country &&
    countrySpecificBuilders[country] &&
    countrySpecificBuilders[country][addressType]
  ) {
    return countrySpecificBuilders[country][addressType](googleAddressComponents) || undefined;
  } else {
    const key = addressKeys.find((key) => googleAddressComponents[key] && googleAddressComponents[key][nameType]);
    return key ? (googleAddressComponents[key][nameType] as string) : undefined;
  }
};

export const mapAddressComponents = (
  addressComponents: Array<AddressComponent>
): AddressComponentMap => {
  const googleAddressComponents = addressComponents.reduce<AddressComponentMap>(
    (
      acc: AddressComponentMap,
      component: AddressComponent
    ): AddressComponentMap => {
      return component.types?.reduce<AddressComponentMap>(
        (acc: AddressComponentMap, type: string): AddressComponentMap => {
          acc[type] = acc[type] ? acc[type] : component;
          return acc;
        },
        acc
      ) || acc;
    },
    {}
  );

  if (!googleAddressComponents["administrative_area_level_1"]) {
    googleAddressComponents["administrative_area_level_1"] = googleAddressComponents["locality"];
  }

  if (!googleAddressComponents["locality"]) {
    googleAddressComponents["locality"] = googleAddressComponents["administrative_area_level_1"];
  }

  return googleAddressComponents;
};

export const formatAddress = (address: PreFormatAddress): Address | undefined => {
  if (address.locality && address.administrative_area_level_1 && address.postal_code && address.country) {
    return {
      place_id: address.place_id,
      address1: `${address.street_number} ${address.route}`,
      city: address.locality,
      state: address.administrative_area_level_1,
      zip: address.postal_code,
      country: address.country,
      latitude: address.lat,
      longitude: address.lng,
      formattedAddress: address.formattedAddress
    };
  }
};

export const getLatLng = (geometry: Geometry): [lat: number | undefined, lng: number | undefined] => {
  if (geometry.location) {
    const {
      location: { lat, lng }
    } = geometry;

    const numericLat: number | undefined = typeof lat === "function" ? lat() : lat;
    const numericLng: number | undefined = typeof lng === "function" ? lng() : lng;

    return [numericLat, numericLng];
  }

  return [undefined, undefined];
};

export const handlePlace = (place: Place): ValidatedAddress => {
  if (place && place.geometry) {
    const {
      address_components: addressComponents,
      place_id: placeId,
      geometry,
      formatted_address: formattedAddress
    } = place;

    const [lat, lng] = getLatLng(geometry);

    if (lat && lng && placeId && addressComponents && formattedAddress) {
      const googleAddressComponents = mapAddressComponents(addressComponents);
      const addressTypes = Object.keys(mainComponentNames) as Array<keyof MainComponentsType>;

      const { isValid, data }: PreFormatValidatedAddress = addressTypes.reduce(
        (acc: PreFormatValidatedAddress, addressType: keyof MainComponentsType): PreFormatValidatedAddress => {
          const nameType = mainComponentNames[addressType]; // short_name or long_name
          acc.data[addressType] = getAddressComponentValue(
            addressType,
            nameType,
            googleAddressComponents
          );
          acc.isValid = Boolean(acc.data[addressType]) && acc.isValid;
          return acc;
        },
        {
          data: { lat, lng, place_id: placeId, formattedAddress },
          isValid: true
        }
      );

      return isValid ? { isValid, address: formatAddress(data) } : { isValid };
    }
  }

  return { isValid: false };
};
